import { useState, useMemo, useEffect } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
    Button,
    Group,
    Heading,
    Level,
    Switch,
    Text,
    TextInput,
    TextInputMode,
    TextType,
} from '@defa/defa-component-library';
import { useNavigate } from 'react-router-dom';
import { BrandPanel } from '@fractions/brand-panel';
import { AuthLayout } from '@fractions/auth-layout';
import i18n from '@i18n';
import { GET_PASSWORD_TOKEN_VALID, NEW_PASSWORD } from './reset-password-new-password.queries';
import { testPasswordPolicy } from '@utils/password-policy';
import { HttpStatusCode } from '../../http-status-codes';

export const ResetPasswordNewPassword = () => {
    const [newPassword, setNewPassword] = useState<string>('');
    const [confirmPassword, setConfirmPassword] = useState<string>('');
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const navigate = useNavigate();
    const params = new URLSearchParams(window.location.search);
    const adminId = params.get('adminId');
    const token = params.get('token');
    const [NewPassword] = useMutation(NEW_PASSWORD);
    const [getPasswordTokenValid, { data: PasswordTokenValidData }] = useLazyQuery(
        GET_PASSWORD_TOKEN_VALID,
        {
            variables: { id: adminId, passwordToken: token },
        }
    );

    const valid = testPasswordPolicy(newPassword) && newPassword === confirmPassword;

    const onNewPasswordChange = useMemo(
        () =>
            (text: string): void => {
                setNewPassword(text);
            },
        [setNewPassword]
    );

    const onConfirmPasswordChange = useMemo(
        () =>
            (text: string): void => {
                setConfirmPassword(text);
            },
        [setConfirmPassword]
    );

    const onShowPasswordChange = useMemo(
        () =>
            (value: boolean): void => {
                setShowPassword(value);
            },
        [setShowPassword]
    );

    useEffect(() => {
        if (token && adminId) {
            getPasswordTokenValid();
        }
    }, [token, adminId, getPasswordTokenValid]);

    useEffect(() => {
        const { getPasswordTokenValid: authResponse } = PasswordTokenValidData || {
            getPasswordTokenValid: null,
        };
        const { status } = authResponse || { status: null };
        if (status && status !== HttpStatusCode.Ok) {
            navigate('/');
        }
    }, [PasswordTokenValidData, navigate]);

    const submit = useMemo(
        () => async () => {
            if (valid) {
                const { data } = await NewPassword({
                    variables: { id: adminId, passwordToken: token, newPass: newPassword },
                });
                const { newPassword: authResponse } = data;
                const { status } = authResponse;
                if (status === HttpStatusCode.Ok) {
                    navigate('/');
                }
            }
        },
        [adminId, token, newPassword, NewPassword, valid, navigate]
    );

    return (
        <AuthLayout
            brand={
                <BrandPanel
                    heading={i18n.t('BrandPanel.Header')}
                    subHeading={i18n.t('BrandPanel.SubHeader')}
                />
            }
        >
            <Group>
                <Group>
                    <Heading level={Level.h1}>{i18n.t('ResetPasswordNewPassword.Header')}</Heading>
                    <Text>{i18n.t('ResetPasswordNewPassword.Description')}</Text>
                </Group>

                <Group>
                    <TextInput
                        label={i18n.t('ResetPasswordNewPassword.NewPasswordLabel')}
                        mode={showPassword ? TextInputMode.PLAIN_TEXT : TextInputMode.PASSWORD}
                        name="newPassword"
                        value={newPassword}
                        message={
                            newPassword.length > 0 &&
                            !testPasswordPolicy(newPassword) &&
                            i18n.t('ResetPasswordNewPassword.PasswordPolicyErrorMessage')
                        }
                        onChange={onNewPasswordChange}
                    />
                    <TextInput
                        label={i18n.t('ResetPasswordNewPassword.ConfirmPasswordLabel')}
                        mode={showPassword ? TextInputMode.PLAIN_TEXT : TextInputMode.PASSWORD}
                        name="confirmPassword"
                        value={confirmPassword}
                        message={
                            confirmPassword.length > 0 &&
                            newPassword !== confirmPassword &&
                            i18n.t('ResetPasswordNewPassword.MessageMatch')
                        }
                        onChange={onConfirmPasswordChange}
                    />
                    <Text type={TextType.description}>
                        {i18n.t('ResetPasswordNewPassword.PasswordDescription')}
                    </Text>
                    <Switch
                        justify="start"
                        reverse
                        label={i18n.t('ResetPasswordNewPassword.ShowPasswordSwitch')}
                        name="show-password-switch"
                        checked={showPassword}
                        onChange={onShowPasswordChange}
                    />
                </Group>
                <Group>
                    <Button
                        text={i18n.t('ResetPasswordNewPassword.SubmitButton')}
                        disabled={!valid}
                        onClick={submit}
                        name="submit"
                    />
                </Group>
            </Group>
        </AuthLayout>
    );
};
